import React from "react";
import { Row, Col, Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faFileInvoiceDollar, 
  faExclamationTriangle, 
  faReceipt, 
  faCoins,
  faChartLine
} from "@fortawesome/free-solid-svg-icons";
import FeatherIcon from "feather-icons-react";

interface InvoiceSummaryCardProps {
  kpiData: {
    logisticTotal: number;
    logisticCount: number;
    penaltyTotal: number;
    penaltyCount: number;
  };
  loadingKPI: boolean;
  formatCurrency: (amount: number) => string;
}

const InvoiceSummaryCard: React.FC<InvoiceSummaryCardProps> = ({
  kpiData,
  loadingKPI,
  formatCurrency
}) => {
  const { t } = useTranslation();
  
  // Calculate total amount
  const totalAmount = kpiData.logisticTotal + kpiData.penaltyTotal;

  return (
    <Row className="g-3 mb-4">
      {/* Total Amount Card */}
      <Col xs={12} md={6} lg={4}>
        <Card className="border-0 rounded-3 h-100 shadow-sm">
          <Card.Body className="p-3">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <div className="rounded-circle p-2 bg-success-subtle me-2 d-flex align-items-center justify-content-center" style={{width: '38px', height: '38px'}}>
                  <FeatherIcon icon="dollar-sign" size={18} className="text-success" />
                </div>
                <h6 className="mb-0 text-body-tertiary">{t("invoices.totalAmount")}</h6>
              </div>
              {/* <span className="badge bg-primary-subtle text-primary rounded-pill fs-9 px-3 py-2">
                {t("invoices.types.all")}
              </span> */}
            </div>
            
            <hr className="my-3" />
            
            <div className="d-flex align-items-center">
              <div>
                <h3 className="mb-0 fw-bold text-success">
                  {loadingKPI ? (
                    <span className="placeholder col-8"></span>
                  ) : (
                    formatCurrency(totalAmount)
                  )}
                </h3>
                <p className="mb-0 text-body-tertiary fs-9 d-flex align-items-center mt-1">
                  <FeatherIcon icon="file-text" size={14} className="me-1 text-success" />
                  {loadingKPI ? (
                    <span className="placeholder col-4"></span>
                  ) : (
                    `${kpiData.logisticCount + kpiData.penaltyCount} ${t("invoices.tableHeaders.reference")}`
                  )}
                </p>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>
      
      {/* Logistic Invoices Card */}
      <Col xs={12} md={6} lg={4}>
        <Card className="border-0 rounded-3 h-100 shadow-sm">
          <Card.Body className="p-3">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <div className="rounded-circle p-2 bg-primary-subtle me-2 d-flex align-items-center justify-content-center" style={{width: '38px', height: '38px'}}>
                  <FeatherIcon icon="truck" size={18} className="text-primary" />
                </div>
                <h6 className="mb-0 text-body-tertiary">{t("invoices.types.logistic")}</h6>
              </div>
              <span className="badge bg-primary-subtle text-primary rounded-pill fs-9 px-3 py-2">
                {kpiData.logisticCount}
              </span>
            </div>
            
            <hr className="my-3" />
            
            <div className="d-flex align-items-center">
              <div>
                <h3 className="mb-0 fw-bold text-primary">
                  {loadingKPI ? (
                    <span className="placeholder col-8"></span>
                  ) : (
                    formatCurrency(kpiData.logisticTotal)
                  )}
                </h3>
                <p className="mb-0 text-body-tertiary fs-9 d-flex align-items-center mt-1">
                  <FeatherIcon icon="percent" size={14} className="me-1 text-primary" />
                  {loadingKPI ? (
                    <span className="placeholder col-4"></span>
                  ) : (
                    `${((kpiData.logisticTotal / totalAmount) * 100).toFixed(1)}% ${t("invoices.totalAmount")}`
                  )}
                </p>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>
      
      {/* Penalty Invoices Card */}
      <Col xs={12} md={6} lg={4}>
        <Card className="border-0 rounded-3 h-100 shadow-sm">
          <Card.Body className="p-3">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <div className="rounded-circle p-2 bg-warning-subtle me-2 d-flex align-items-center justify-content-center" style={{width: '38px', height: '38px'}}>
                  <FeatherIcon icon="alert-triangle" size={18} className="text-warning" />
                </div>
                <h6 className="mb-0 text-body-tertiary">{t("invoices.types.penalty")}</h6>
              </div>
              <span className="badge bg-warning-subtle text-warning rounded-pill fs-9 px-3 py-2">
                {kpiData.penaltyCount}
              </span>
            </div>
            
            <hr className="my-3" />
            
            <div className="d-flex align-items-center">
              <div>
                <h3 className="mb-0 fw-bold text-warning">
                  {loadingKPI ? (
                    <span className="placeholder col-8"></span>
                  ) : (
                    formatCurrency(kpiData.penaltyTotal)
                  )}
                </h3>
                <p className="mb-0 text-body-tertiary fs-9 d-flex align-items-center mt-1">
                  <FeatherIcon icon="percent" size={14} className="me-1 text-warning" />
                  {loadingKPI ? (
                    <span className="placeholder col-4"></span>
                  ) : (
                    `${((kpiData.penaltyTotal / totalAmount) * 100).toFixed(1)}% ${t("invoices.totalAmount")}`
                  )}
                </p>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>
      
      {/* Extra space - we only show 3 cards */}
    </Row>
  );
};

export default InvoiceSummaryCard;
